import React, { useState } from 'react';
import './styles/faq.css';

const FAQ = ({ 
  faqData: propFaqData, 
  title = "Ofte Stillede Spørgsmål",
  subtitle = "Find svar på de mest almindelige spørgsmål om vores hjemmeside- og SEO-tjenester",
  className = ""
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // SEO-optimeret FAQ data med struktureret indhold - bruger props hvis de er passed
  const defaultFaqData = [
    {
      question: "Hvad koster det at få lavet en hjemmeside?",
      answer: "Prisen på en hjemmeside afhænger af flere faktorer som kompleksitet, design, funktionalitet og størrelse. Vores priser starter fra 10.000 kr. (ekskl. moms) for en grundlæggende hjemmeside og kan gå op til 75.000 kr. (ekskl. moms) for komplekse webshop-løsninger. Vi tilbyder altid et gratis og uforpligtende tilbud baseret på dine specifikke behov.",
      keywords: ["hjemmeside pris", "website udvikling", "web design pris"]
    },
    {
      question: "Hvor lang tid tager det at udvikle en hjemmeside?",
      answer: "Udviklingen af en hjemmeside tager typisk mellem 2-8 uger, afhængigt af projektets omfang. En simpel hjemmeside kan være klar på 2-3 uger, mens mere komplekse løsninger med webshop eller specialfunktioner kan tage 6-8 uger. Vi holder dig opdateret gennem hele processen.",
      keywords: ["hjemmeside udvikling tid", "website levering", "web udvikling proces"]
    },
    {
      question: "Får jeg hjælp til SEO optimering?",
      answer: "Ja, alle vores hjemmesider kommer med grundlæggende SEO-optimering inkluderet. Dette omfatter meta-tags, strukturerede data, mobiloptimering, hurtig indlæsning og søgemaskinevenlig struktur. Vi tilbyder også avancerede SEO-tjenester som keyword research, content optimering og løbende SEO-vedligeholdelse.",
      keywords: ["SEO optimering", "søgemaskineoptimering", "Google ranking"]
    },
    {
      question: "Hvad er inkluderet i jeres hosting og vedligeholdelse?",
      answer: "Vores hosting inkluderer sikker cloud-hosting, automatiske backups, SSL-certifikat, opdateringer, sikkerhedsovervågning og teknisk support. Vi tilbyder også content-opdateringer, performance-optimering og løbende sikkerhedsopdateringer. Hosting starter fra 299 kr./måned (ekskl. moms).",
      keywords: ["website hosting", "hjemmeside vedligeholdelse", "web support"]
    },
    {
      question: "Kan I lave webshop og e-commerce løsninger?",
      answer: "Absolut! Vi specialiserer os i at skabe professionelle webshop-løsninger med integrerede betalingssystemer, lagerlogistik, produktkatalog og kundeadministration. Vores e-commerce løsninger er mobile-optimerede og SEO-venlige, og vi integrerer med populære betalingsudbydere som Stripe, PayPal og MobilePay.",
      keywords: ["webshop udvikling", "e-commerce løsning", "online butik"]
    },
    {
      question: "Bliver min hjemmeside mobilvenlig og responsiv?",
      answer: "Ja, alle vores hjemmesider er bygget med mobile-first princippet og er fuldt responsive på alle enheder - mobil, tablet og desktop. Vi sikrer optimal brugeroplevelse på alle skærmstørrelser og tester grundigt på forskellige enheder før levering.",
      keywords: ["responsiv design", "mobilvenlig hjemmeside", "mobile optimering"]
    },
    {
      question: "Kan jeg selv redigere indholdet på hjemmesiden?",
      answer: "Nej, vi håndterer alle opdateringer og ændringer på hjemmesiden selv. Som eksperter sikrer vi, at alt fungerer optimalt og ser professionelt ud. Når du har brug for opdateringer til tekst, billeder eller indhold, kontakter du os, og vi klarer det hurtigt og effektivt. Dette sikrer konsistent kvalitet og optimal performance.",
      keywords: ["hjemmeside opdateringer", "professionel vedligeholdelse", "web service"]
    },
    {
      question: "Hvad sker der hvis jeg ikke er tilfreds med resultatet?",
      answer: "Vi er eksperter inden for webdesign og SEO, og vi leverer altid professionelle løsninger baseret på bedste praksis og markedsstandarder. Du kan naturligvis komme med input og feedback, men vi fokuserer på at levere en hjemmeside der fungerer optimalt og konverterer godt - ikke på at imødekomme alle ønsker som kan skade sidens performance.",
      keywords: ["professionel webdesign", "ekspert løsning", "optimal performance"]
    }
  ];

  // Brug propFaqData hvis det er passed, ellers brug defaultFaqData
  const faqData = propFaqData || defaultFaqData;

  // Schema.org struktureret data for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <section className={`faq-section ${className}`} id="faq" aria-labelledby="faq-heading">
      {/* Schema.org JSON-LD for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <div className="container">
        <div className="faq-header">
          <h2 id="faq-heading" className="faq-title">
            {title}
          </h2>
          <p className="faq-subtitle">
            {subtitle}
          </p>
        </div>

        <div className="faq-content">
          <div className="faq-accordion" role="region" aria-labelledby="faq-heading">
            {faqData.map((item, index) => (
              <div
                key={index}
                className={`faq-item ${activeIndex === index ? 'active' : ''}`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  className="faq-question"
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={activeIndex === index}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span itemProp="name">{item.question}</span>
                  <span className="faq-icon" aria-hidden="true">
                    {activeIndex === index ? '−' : '+'}
                  </span>
                </button>
                
                <div
                  id={`faq-answer-${index}`}
                  className="faq-answer"
                  aria-labelledby={`faq-question-${index}`}
                  role="region"
                  itemScope
                  itemType="https://schema.org/Answer"
                >
                  <div className="faq-answer-content">
                    <p itemProp="text">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
